/**
 * Credential profiles (D-44): every named set of credentials, which apps use it, and a way to make
 * a new one, typically by copying Production and swapping in mock or backup credentials.
 */
import { ChevronRight, Layers, Plus } from "@gravity-ui/icons";
import { Button, Card, Chip, toast } from "@heroui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { FormDialog } from "../components/dialogs";
import { SelectInput, TextAreaInput, TextInput } from "../components/fields";
import { ChannelChip, EmptyBlock, ErrorBlock, LoadingBlock, Page, PageHeader } from "../components/ui";
import { useCreateProfile, useMe, useProfiles } from "../lib/queries";

export function CredentialProfilesPage() {
  const { t } = useTranslation();
  const profiles = useProfiles();
  const { data: me } = useMe();
  const canEdit = me?.role !== "viewer";
  const [createOpen, setCreateOpen] = useState(false);

  const newButton = canEdit ? (
    <Button onPress={() => setCreateOpen(true)}>
      <Plus />
      {t("profiles.new")}
    </Button>
  ) : null;

  return (
    <Page>
      <PageHeader title={t("profiles.title")} description={t("profiles.subtitle")} actions={newButton} />
      {profiles.isPending ? (
        <LoadingBlock />
      ) : profiles.isError ? (
        <ErrorBlock error={profiles.error} onRetry={() => profiles.refetch()} />
      ) : profiles.data.length === 0 ? (
        <Card>
          <EmptyBlock
            icon={Layers}
            title={t("profiles.empty")}
            hint={t("profiles.emptyHint")}
            action={newButton}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {profiles.data.map((p) => (
            <Link
              key={p.id}
              to={`/credential-profiles/${p.id}`}
              className="group rounded-[28px] outline-none focus-visible:ring-2 focus-visible:ring-focus"
            >
              <Card className="h-full gap-5 p-6 transition-shadow group-hover:shadow-md">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="truncate text-base font-semibold">{p.name}</span>
                    {p.description ? (
                      <span className="line-clamp-2 text-sm text-muted">{p.description}</span>
                    ) : null}
                  </div>
                  <ChevronRight className="size-4 shrink-0 text-muted transition-transform group-hover:translate-x-0.5" />
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-xs text-muted">{t("profiles.channels")}</span>
                  {p.channels.length ? (
                    <ul className="flex flex-col gap-1.5">
                      {p.channels.map((c) => (
                        <li key={c.channel} className="flex flex-wrap items-center gap-2">
                          <ChannelChip channel={c.channel} />
                          <span className="truncate font-mono text-xs text-muted">
                            {c.aliases.join(", ")}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-sm text-muted">{t("assignments.empty")}</span>
                  )}
                </div>
                <div className="mt-auto flex flex-col gap-2">
                  <span className="text-xs text-muted">{t("profiles.usedBy")}</span>
                  {p.apps.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {p.apps.map((a) => (
                        <Chip key={a.id} size="sm" variant="secondary">
                          {a.name}
                        </Chip>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-muted">{t("profiles.noApps")}</span>
                  )}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
      <CreateProfileDialog isOpen={createOpen} onOpenChange={setCreateOpen} />
    </Page>
  );
}

function CreateProfileDialog({
  isOpen,
  onOpenChange,
}: {
  isOpen: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const profiles = useProfiles();
  const create = useCreateProfile();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [copyFrom, setCopyFrom] = useState<string | null>("none");

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when the dialog opens.
  useEffect(() => {
    if (isOpen) {
      setName("");
      setDescription("");
      setCopyFrom("none");
      create.reset();
    }
  }, [isOpen]);

  return (
    <FormDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={t("profiles.createTitle")}
      submitLabel={t("common.create")}
      isPending={create.isPending}
      isSubmitDisabled={!name.trim()}
      error={create.error}
      onSubmit={() =>
        create.mutate(
          {
            name: name.trim(),
            description: description.trim() || null,
            copy_from: copyFrom && copyFrom !== "none" ? copyFrom : undefined,
          },
          {
            onSuccess: (profile) => {
              onOpenChange(false);
              toast.success(t("profiles.created"));
              navigate(`/credential-profiles/${profile.id}`);
            },
          },
        )
      }
    >
      <TextInput
        name="profile-name"
        label={t("profiles.name")}
        placeholder={t("profiles.namePlaceholder")}
        value={name}
        onChange={setName}
        autoFocus
        isRequired
      />
      <TextAreaInput
        name="profile-description"
        label={`${t("profiles.description")} (${t("common.optional").toLowerCase()})`}
        placeholder={t("profiles.descriptionPlaceholder")}
        value={description}
        onChange={setDescription}
        rows={2}
      />
      <SelectInput
        label={t("profiles.copyFrom")}
        description={t("profiles.copyFromHint")}
        value={copyFrom}
        onChange={setCopyFrom}
        options={[
          { id: "none", label: t("profiles.copyFromNone") },
          ...(profiles.data ?? []).map((p) => ({
            id: p.id,
            label: p.name,
            hint: p.channels.map((c) => t(`channels.${c.channel}`)).join(", ") || undefined,
          })),
        ]}
      />
    </FormDialog>
  );
}
